import { useState, useRef, useCallback, useEffect } from "react";
import { TablePosition, TableShape } from "@/hooks/useFloorPlanPositions";

interface DragState {
  type: 'table' | 'pan' | 'resize';
  tableId?: string;
  startX: number;
  startY: number;
  origX?: number;
  origY?: number;
  origPanX?: number;
  origPanY?: number;
  origW?: number;
  origH?: number;
  isDragging: boolean;
  pointerId: number;
}

interface UseFloorPlanEditorOptions {
  positions: TablePosition[];
  cellSize: number;
  panOffset: { x: number; y: number };
  setPanOffset: (offset: { x: number; y: number }) => void;
  onPositionChange: (pos: TablePosition) => void;
  allowOverlap: boolean;
  checkCollision: (pos: TablePosition, excludeId?: string) => boolean;
}

export const useFloorPlanEditor = ({
  positions,
  cellSize,
  panOffset,
  setPanOffset,
  onPositionChange,
  allowOverlap,
  checkCollision,
}: UseFloorPlanEditorOptions) => {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [dragTransform, setDragTransform] = useState<{ tableId: string; dx: number; dy: number } | null>(null);
  const [resizeTransform, setResizeTransform] = useState<{ tableId: string; dw: number; dh: number } | null>(null);

  // Use refs for callbacks to avoid stale closures in pointer handlers
  const positionsRef = useRef(positions);
  const panOffsetRef = useRef(panOffset);
  const cellSizeRef = useRef(cellSize);
  const allowOverlapRef = useRef(allowOverlap);
  const checkCollisionRef = useRef(checkCollision);
  const onPositionChangeRef = useRef(onPositionChange);
  const setPanOffsetRef = useRef(setPanOffset);

  useEffect(() => { positionsRef.current = positions; }, [positions]);
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
  useEffect(() => { cellSizeRef.current = cellSize; }, [cellSize]);
  useEffect(() => { allowOverlapRef.current = allowOverlap; }, [allowOverlap]);
  useEffect(() => { checkCollisionRef.current = checkCollision; }, [checkCollision]);
  useEffect(() => { onPositionChangeRef.current = onPositionChange; }, [onPositionChange]);
  useEffect(() => { setPanOffsetRef.current = setPanOffset; }, [setPanOffset]);

  const DRAG_THRESHOLD = 4;

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;

    // Check for resize handle
    const resizeHandle = target.closest('[data-resize-handle]');
    if (resizeHandle) {
      const tableEl = target.closest('[data-table-id]');
      if (!tableEl) return;
      const tableId = tableEl.getAttribute('data-table-id')!;
      const pos = positionsRef.current.find(p => p.tableId === tableId);
      if (!pos) return;

      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      dragRef.current = {
        type: 'resize',
        tableId,
        startX: e.clientX,
        startY: e.clientY,
        origW: pos.width,
        origH: pos.height,
        isDragging: false,
        pointerId: e.pointerId,
      };
      return;
    }

    // Check for no-drag elements (toolbar buttons)
    if (target.closest('[data-no-drag]')) return;

    const tableEl = target.closest('[data-table-id]');
    if (tableEl) {
      const tableId = tableEl.getAttribute('data-table-id')!;
      const pos = positionsRef.current.find(p => p.tableId === tableId);
      if (!pos) return;

      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      dragRef.current = {
        type: 'table',
        tableId,
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
        isDragging: false,
        pointerId: e.pointerId,
      };
    } else {
      // Pan canvas
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      dragRef.current = {
        type: 'pan',
        startX: e.clientX,
        startY: e.clientY,
        origPanX: panOffsetRef.current.x,
        origPanY: panOffsetRef.current.y,
        isDragging: false,
        pointerId: e.pointerId,
      };
    }
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    if (!drag.isDragging) {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      drag.isDragging = true;
    }

    if (drag.type === 'table') {
      setDragTransform({ tableId: drag.tableId!, dx, dy });
    } else if (drag.type === 'pan') {
      setPanOffsetRef.current({
        x: drag.origPanX! + dx,
        y: drag.origPanY! + dy,
      });
    } else if (drag.type === 'resize') {
      setResizeTransform({ tableId: drag.tableId!, dw: dx, dh: dy });
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(drag.pointerId);
    } catch {
      // Pointer capture may already be released
    }

    if (drag.type === 'table') {
      if (drag.isDragging) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        const cs = cellSizeRef.current;

        const newX = drag.origX! + dx / cs;
        const newY = drag.origY! + dy / cs;

        // Snap to 0.5 grid units for visual precision, rounded to int on save
        const snapUnit = 0.5;
        const snappedX = Math.max(0, Math.round(newX / snapUnit) * snapUnit);
        const snappedY = Math.max(0, Math.round(newY / snapUnit) * snapUnit);

        const pos = positionsRef.current.find(p => p.tableId === drag.tableId);
        if (pos) {
          // Stabilisation Phase 1 : on applique TOUJOURS le déplacement.
          // La collision est tolérée et signalée visuellement (overlap rouge),
          // mais ne bloque plus silencieusement le mouvement.
          const newPos = { ...pos, x: snappedX, y: snappedY };
          onPositionChangeRef.current(newPos);
        }
      } else {
        // Click → select/deselect
        setSelectedTableId(prev => prev === drag.tableId! ? null : drag.tableId!);
      }
      setDragTransform(null);
    } else if (drag.type === 'resize') {
      if (drag.isDragging) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        const cs = cellSizeRef.current;

        const finalW = Math.max(1, Math.min(4, Math.round(drag.origW! + dx / cs)));
        const finalH = Math.max(1, Math.min(4, Math.round(drag.origH! + dy / cs)));

        const pos = positionsRef.current.find(p => p.tableId === drag.tableId);
        if (pos) {
          // Idem pour le redimensionnement : toujours appliqué.
          const newPos = { ...pos, width: finalW, height: finalH };
          onPositionChangeRef.current(newPos);
        }
      }
      setResizeTransform(null);
    }

    dragRef.current = null;
  }, []);

  const handleShapeChange = useCallback((tableId: string, shape: TableShape) => {
    const pos = positionsRef.current.find(p => p.tableId === tableId);
    if (!pos) return;
    onPositionChangeRef.current({ ...pos, shape });
  }, []);

  const handleRotate = useCallback((tableId: string) => {
    const pos = positionsRef.current.find(p => p.tableId === tableId);
    if (!pos) return;
    const newRotation = ((pos.rotation || 0) + 45) % 360;
    onPositionChangeRef.current({ ...pos, rotation: newRotation });
  }, []);

  return {
    selectedTableId,
    setSelectedTableId,
    dragTransform,
    resizeTransform,
    handleShapeChange,
    handleRotate,
    canvasPointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
  };
};
